import { useMemo, type MouseEvent, type ReactNode } from "react";
import { PendingInboxItem } from "@forgepoint/shared";
import { DataTable } from "@/components/ui/data-table";
import { buildInboxTableColumns } from "@/pages/inboxTableColumns";
import type { InboxTableRow } from "@/pages/inboxTableRows";

export interface InboxTableProps {
  rows: InboxTableRow[];
  isLoading: boolean;
  currentUserId?: string;
  showStatus: boolean;
  onClickRow: (row: InboxTableRow) => void;
  canCancel: (item: PendingInboxItem) => boolean;
  onCancel: (e: MouseEvent, item: PendingInboxItem) => void;
  emptyState: ReactNode;
}

/**
 * The task inbox rendered as a flat table: one row per task, in the order the
 * rows were built. Fills the space below the inbox header; rows scroll inside.
 * @param rows - The rows to list
 * @param isLoading - Whether to show skeleton rows instead of content
 * @param currentUserId - The signed-in user's id, when known
 * @param showStatus - Whether to render the status column
 * @param onClickRow - Called with the row that was clicked
 * @param canCancel - Whether the signed-in user may cancel a given agent task
 * @param onCancel - Called when an agent row's cancel button is clicked
 * @param emptyState - What to show when there are no rows
 * @returns The inbox table
 */
export function InboxTable({
  rows,
  isLoading,
  currentUserId,
  showStatus,
  onClickRow,
  canCancel,
  onCancel,
  emptyState,
}: InboxTableProps) {
  const columns = useMemo(
    () =>
      buildInboxTableColumns({
        currentUserId,
        showStatus,
        canCancel,
        onCancel,
      }),
    [currentUserId, showStatus, canCancel, onCancel],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        hideSearch
        className="min-h-0 [&_table]:min-w-[900px] [&_table]:table-fixed"
        onClickRow={onClickRow}
        emptyState={emptyState}
      />
    </div>
  );
}
